import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initializeSession,
  getCurrentSession,
  getStoredSession,
  shouldShowValidation,
  markValidationShown,
  exchangeHandoffFromUrl
} from '../utils/sessionManager';
import { monitorInternetStatus } from '../utils/internetUtils';
import authService from '../services/auth.service';
import sessionService from '../services/session.service';
import { buildOrgPath } from '../utils/routePaths';

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const navigate = useNavigate();

  // State from original SessionContext
  const [session, setSession] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // State migrated from App.js
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [showValidation, setShowValidation] = useState(false);

  // OPUS-1106: surfaced when the ?h= handoff exchange fails because of a
  // network problem (as opposed to an invalid/expired link) so the UI can
  // show a recoverable error instead of spinning forever or silently landing
  // on "No session found". retryToken re-runs the init effect below.
  const [connectionError, setConnectionError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  // Initialize Session Logic
  useEffect(() => {
    let cancelled = false;

    const initializeApp = async () => {
      setConnectionError(null);
      const currentUrl = window.location.href;

      // OPUS-901/902: secure handoff — exchange a ?h= token for a real session
      // (refresh token in the response body, not the URL) before the legacy path.
      if (currentUrl.includes('h=')) {
        const result = await exchangeHandoffFromUrl();
        if (cancelled) return;

        if (result && result.orgId) {
          setSession(result);
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        // OPUS-1106: a genuine network failure must not fall through to the
        // legacy ?session= path — that's what used to silently misreport as
        // "no session". An invalid/expired handoff (result.error === 'invalid',
        // or the legacy `null`) still falls through unchanged below.
        if (result && result.error === 'network') {
          setConnectionError(result.message);
          setIsLoading(false);
          return;
        }
      }

      const hasSessionParam = currentUrl.includes('session=');

      const sessionData = initializeSession(currentUrl);

      if (sessionData && sessionData.orgId) {
        setSession(sessionData);
        const verifySession = localStorage.getItem('aedrive_session_data');

        // Case 1: Session not in local storage yet
        if (!verifySession) {
          try {
            localStorage.setItem('aedrive_session_data', JSON.stringify(sessionData));
            const verifyAgain = localStorage.getItem('aedrive_session_data');
            if (!verifyAgain) {
              // localStorage write claimed success but the value isn't there —
              // private-browsing quota or extension interference. Drop the
              // loading + validation spinners so the UI doesn't hang.
              setShowValidation(false);
              setIsLoading(false);
              setIsInitialized(true);
              return;
            }
          } catch (error) {
            // Same fallback as above — full lock-down (storage quota / private
            // mode). Without this both spinners stay on indefinitely.
            console.error('Failed to persist session to localStorage', error);
            setShowValidation(false);
            setIsLoading(false);
            setIsInitialized(true);
            return;
          }
        }

        // Case 2: Validation Check
        if (shouldShowValidation(sessionData)) {
          setShowValidation(true);
          setIsLoading(false);
          // Note: App.js had 'isValidating=true' and 'isCheckingSession=false' implicitly
          // Here we just say we are done loading, but validation determines what is shown.
        } else {
          // No validation needed, clear URL params and proceed
          handleUrlCleanup(currentUrl, sessionData, hasSessionParam);
          setIsLoading(false);

          // If we are already authenticated/have session, maybe triggering a check right away?
          // App.js logic was: if !shouldShowValidation -> setIsValidating(false), setSessionInitialized(true)
          // But it didn't automatically check for existing server session in that branch? 
          // Wait, App.js ONLY checks for existing active session inside 'handleValidationComplete'.
          // If validation was NOT shown, App.js just rendered the routes.
        }
      } else {
        // Retry logic from App.js
        if (hasSessionParam) {
          setTimeout(() => {
            const retrySession = initializeSession(window.location.href);
            if (retrySession && retrySession.orgId) {
              setSession(retrySession);
              const cleanPath = buildOrgPath(retrySession.orgId);
              navigateClean(cleanPath);
            }
            setIsLoading(false);
          }, 500);
        } else {
          // Check if we have a stored session
          const stored = getCurrentSession();
          if (stored) setSession(stored);
          setIsLoading(false);
        }
      }
      setIsInitialized(true);
    };

    initializeApp();

    return () => {
      cancelled = true;
    };
  }, [navigate, retryToken]);

  // OPUS-1106: while a connection error is showing, listen for the browser
  // coming back online and automatically retry the handoff exchange. Only
  // mounted while connectionError is set, so there's no always-on listener
  // in the normal (no-error) case, and retries are purely event-driven
  // (no polling/timers), so flapping connectivity can't cause a retry loop.
  useEffect(() => {
    if (!connectionError) return undefined;
    return monitorInternetStatus((status) => {
      if (status.online) {
        setRetryToken((t) => t + 1);
      }
    });
  }, [connectionError]);

  const retryConnection = useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);

  // Auth Token Refresh Effect (from original SessionContext)
  useEffect(() => {
    if ((session || isInitialized) && authService.isAuthenticated()) {
      authService.startTokenRefreshInterval();
    }
    return () => {
      authService.stopTokenRefreshInterval();
    };
  }, [session, isInitialized]);

  // Helpers
  const navigateClean = (path) => {
    window.history.replaceState({}, '', path);
    navigate(path, { replace: true });
  };

  const handleUrlCleanup = (currentUrl, sessionData, hasSessionParam) => {
    if (hasSessionParam) {
      setTimeout(() => {
        try {
          const urlObj = new URL(currentUrl);
          const currentPath = urlObj.pathname;

          if (currentPath === '/' || currentPath === '') {
            const cleanPath = buildOrgPath(sessionData.orgId);
            navigateClean(cleanPath);
          } else {
            urlObj.searchParams.delete('session');
            const cleanUrl = urlObj.pathname + (urlObj.search || '');
            window.history.replaceState({}, '', cleanUrl);
          }
        } catch (error) {
          const cleanPath = buildOrgPath(sessionData.orgId);
          navigateClean(cleanPath);
        }
      }, 100);
    }
  };

  // Actions
  const completeValidation = async () => {
    const currentSession = getCurrentSession();
    if (currentSession?.orgId) {
      markValidationShown(currentSession);
      setShowValidation(false);

      // Short URL visitors already have a fully-resolved session baked into the
      // ?session= payload. Skip the backend session-creation flow entirely so
      // AE Drive doesn't conflict with or overwrite the assigned session.
      if (currentSession.isShortUrl) {
        setIsLoading(false);
        const { orgId, fixtureId, rendererId, fixture_id, renderer_id } = currentSession;
        const fId = fixtureId || fixture_id;
        const rId = rendererId || renderer_id;
        const path = buildOrgPath(orgId, { fixtureId: fId, rendererId: rId });
        navigateClean(path);
        return;
      }

      await createNewSession(currentSession);
    } else {
      setIsLoading(false);
    }
  };

  const createNewSession = async (currentSession = session) => {
    setIsLoading(true);
    setLoadingMessage('Initializing new session...');

    try {
      await sessionService.createOrUpdateSession();
      setIsLoading(false);

      if (currentSession?.orgId) {
        const cleanPath = buildOrgPath(currentSession.orgId);
        navigateClean(cleanPath);
      }
    } catch (error) {
      console.error('Error creating new session:', error);
      setIsLoading(false);
    }
  };

  const refreshSession = () => {
    const current = getStoredSession();
    setSession(current);
  };

  const value = {
    session,
    isInitialized,
    isLoading,
    loadingMessage,
    showValidation,
    connectionError,
    retryConnection,
    refreshSession,
    completeValidation,
    createNewSession,
    // Helper accessors
    orgId: session?.orgId,
    deviceId: session?.deviceId,
    accessKey: session?.accessKey,
    username: session?.username,
    userId: session?.userId,
    sessionId: session?.sessionId,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

export default SessionContext;
